import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { omitProps, getElementType, callRenderProp, matchComponentTypes } from '@instructure/ui-react-utils';
import { themeable } from '@instructure/ui-themeable';
import { testable } from '@instructure/ui-testable';
var styles = {
  componentId: 'eqmZq',
  template: function template(theme) {
    return "\n\n.eqmZq_bGBk{-ms-user-select:none;-webkit-user-select:none;background:".concat(theme.background || 'inherit', ";color:").concat(theme.color || 'inherit', ";cursor:pointer;display:block;font-family:").concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.mediumFontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";outline:none;position:relative;transition:background 200ms;user-select:none}\n\n.eqmZq_crZr{display:block;padding:").concat(theme.padding || 'inherit', "}\n\n.eqmZq_caGd{align-items:center;display:flex;fill:").concat(theme.iconColor || 'inherit', ";height:100%;pointer-events:none;position:absolute;top:0}\n\n.eqmZq_caGd.eqmZq_fgsM{offset-inline-end:auto;offset-inline-start:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=ltr] .eqmZq_caGd.eqmZq_fgsM{left:").concat(theme.iconPadding || 'inherit', ";right:auto}\n\n[dir=rtl] .eqmZq_caGd.eqmZq_fgsM{left:auto;right:").concat(theme.iconPadding || 'inherit', "}\n\n.eqmZq_caGd.eqmZq_cGRw{offset-inline-end:").concat(theme.iconPadding || 'inherit', ";offset-inline-start:auto}\n\n[dir=ltr] .eqmZq_caGd.eqmZq_cGRw{left:auto;right:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=rtl] .eqmZq_caGd.eqmZq_cGRw{left:").concat(theme.iconPadding || 'inherit', ";right:auto}\n\n.eqmZq_cbMJ{background:").concat(theme.highlightedBackground || 'inherit', "}\n\n.eqmZq_cbMJ,.eqmZq_dDxn{color:").concat(theme.highlightedLabelColor || 'inherit', "}\n\n.eqmZq_dDxn{background:").concat(theme.selectedBackground || 'inherit', "}\n\n.eqmZq_bZuE{cursor:not-allowed;opacity:0.5}\n\n.eqmZq_cNUG{cursor:default}\n\n.eqmZq_cNUG>.eqmZq_crZr{padding:0}\n\n.eqmZq_ePLU .eqmZq_crZr{-webkit-padding-end:").concat(theme.iconPadding || 'inherit', ";-webkit-padding-start:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-inline-end:").concat(theme.iconPadding || 'inherit', ";padding-inline-start:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n[dir=ltr] .eqmZq_ePLU .eqmZq_crZr{padding-left:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-right:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=rtl] .eqmZq_ePLU .eqmZq_crZr{padding-left:").concat(theme.iconPadding || 'inherit', ";padding-right:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n.eqmZq_bRPx .eqmZq_crZr{-webkit-padding-end:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);-webkit-padding-start:").concat(theme.iconPadding || 'inherit', ";padding-inline-end:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-inline-start:").concat(theme.iconPadding || 'inherit', "}\n\n[dir=ltr] .eqmZq_bRPx .eqmZq_crZr{padding-left:").concat(theme.iconPadding || 'inherit', ";padding-right:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n[dir=rtl] .eqmZq_bRPx .eqmZq_crZr{padding-left:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-right:").concat(theme.iconPadding || 'inherit', "}\n\n.eqmZq_bRPx.eqmZq_ePLU .eqmZq_crZr{-webkit-padding-end:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);-webkit-padding-start:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-inline-end:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-inline-start:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n[dir=ltr] .eqmZq_bRPx.eqmZq_ePLU .eqmZq_crZr{padding-left:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-right:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n[dir=rtl] .eqmZq_bRPx.eqmZq_ePLU .eqmZq_crZr{padding-left:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em);padding-right:calc(").concat(theme.iconPadding || 'inherit', "*2 + 1em)}\n\n.eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_crZr{-webkit-padding-start:").concat(theme.nestedPadding || 'inherit', ";padding-inline-start:").concat(theme.nestedPadding || 'inherit', "}\n\n[dir=ltr] .eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_crZr{padding-left:").concat(theme.nestedPadding || 'inherit', "}\n\n[dir=rtl] .eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_crZr{padding-right:").concat(theme.nestedPadding || 'inherit', "}\n\n.eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_fgsM{offset-inline-start:").concat(theme.nestedPadding || 'inherit', "}\n\n[dir=ltr] .eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_fgsM{left:").concat(theme.nestedPadding || 'inherit', "}\n\n[dir=rtl] .eqmZq_bGBk .eqmZq_bGBk:not(.eqmZq_ePLU) .eqmZq_fgsM{right:").concat(theme.nestedPadding || 'inherit', "}");
  },
  'root': 'eqmZq_bGBk',
  'container': 'eqmZq_crZr',
  'content': 'eqmZq_caGd',
  'content--before': 'eqmZq_fgsM',
  'content--after': 'eqmZq_cGRw',
  'isHighlighted': 'eqmZq_cbMJ',
  'isSelected': 'eqmZq_dDxn',
  'isDisabled': 'eqmZq_bZuE',
  'containsList': 'eqmZq_cNUG',
  'hasContentBeforeLabel': 'eqmZq_ePLU',
  'hasContentAfterLabel': 'eqmZq_bRPx'
};
import theme from "./theme.js";
/**
---
parent: Options
id: Options.Item
---
**/

var Item = (_dec = testable(), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Item, _Component);

  var _super = _createSuper(Item);

  function Item() {
    _classCallCheck(this, Item);

    return _super.apply(this, arguments);
  }

  _createClass(Item, [{
    key: "renderContent",
    value: function renderContent(placement) {
      var _classes;

      var _this$props = this.props,
          renderBeforeLabel = _this$props.renderBeforeLabel,
          renderAfterLabel = _this$props.renderAfterLabel;
      var classes = (_classes = {}, _defineProperty(_classes, styles.content, true), _defineProperty(_classes, styles["content--".concat(placement)], true), _classes);
      return /*#__PURE__*/React.createElement("span", {
        className: classnames(classes),
        role: "presentation",
        "aria-hidden": "true"
      }, placement === 'before' && callRenderProp(renderBeforeLabel), placement === 'after' && callRenderProp(renderAfterLabel));
    }
  }, {
    key: "render",
    value: function render() {
      var _classes2;

      var _this$props2 = this.props,
          as = _this$props2.as,
          role = _this$props2.role,
          variant = _this$props2.variant,
          renderBeforeLabel = _this$props2.renderBeforeLabel,
          renderAfterLabel = _this$props2.renderAfterLabel,
          children = _this$props2.children;
      var ElementType = getElementType(Item, this.props, function () {
        return as;
      });
      var passthroughProps = omitProps(this.props, Item.propTypes);
      var classes = (_classes2 = {}, _defineProperty(_classes2, styles.root, true), _defineProperty(_classes2, styles.isDisabled, variant === 'disabled'), _defineProperty(_classes2, styles.isHighlighted, variant === 'highlighted'), _defineProperty(_classes2, styles.isSelected, variant === 'selected'), _defineProperty(_classes2, styles.containsList, this.containsList), _defineProperty(_classes2, styles.hasContentBeforeLabel, renderBeforeLabel), _defineProperty(_classes2, styles.hasContentAfterLabel, renderAfterLabel), _classes2);
      return /*#__PURE__*/React.createElement(ElementType, {
        role: "none",
        className: classnames(classes)
      }, /*#__PURE__*/React.createElement("span", Object.assign({}, passthroughProps, {
        className: styles.container,
        role: role
      }), children), renderBeforeLabel && this.renderContent('before'), renderAfterLabel && this.renderContent('after'));
    }
  }, {
    key: "containsList",
    get: function get() {
      if (matchComponentTypes(this.props.children, ['Options'])) {
        return true;
      }

      return false;
    }
  }]);

  Item.displayName = "Item";
  return Item;
}(Component), _class2.propTypes = {
  /**
   * Element type to render as
   */
  as: PropTypes.elementType,

  /**
   * The style variant of the item
   */
  variant: PropTypes.oneOf(['default', 'highlighted', 'selected', 'disabled']),

  /**
   * The aria role of the element
   */
  role: PropTypes.string,

  /**
   * Content to render before the label
   */
  renderBeforeLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * Content to render after the label
   */
  renderAfterLabel: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func])
}, _class2.defaultProps = {
  as: 'span',
  variant: 'default',
  role: 'listitem',
  renderBeforeLabel: null,
  renderAfterLabel: null,
  children: null
}, _temp)) || _class) || _class);
export default Item;
export { Item };